import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { offboardingAPI, reviewsAPI, applicationsAPI } from '../lib/firestore';
import { useAuth } from '../contexts/AuthContext';
import type { OffboardingRequest, AccessReview, Application } from '../types';
import { AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { formatDate } from '../lib/utils';

interface PendingOffboarding {
  offboarding: OffboardingRequest;
  myApplications: Array<{
    appId: string;
    appName: string;
    pendingCount: number;
  }>;
}

export const MyTasksPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myApplications, setMyApplications] = useState<Application[]>([]);
  const [pendingOffboardings, setPendingOffboardings] = useState<PendingOffboarding[]>([]);
  const [myReviews, setMyReviews] = useState<AccessReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPendingActions, setTotalPendingActions] = useState(0);

  useEffect(() => {
    if (user?.email) {
      loadMyTasks();
    }
  }, [user]);

  const loadMyTasks = async () => {
    if (!user?.email) return;

    const userEmail = user.email;

    try {
      setLoading(true);

      // Get all applications where I'm an admin
      const allApps = await applicationsAPI.getAll();
      const myApps = allApps.filter((app) => app.admins.includes(userEmail));
      setMyApplications(myApps);

      // Get all offboarding requests
      const allOffboardings = await offboardingAPI.getAll();

      // Filter offboardings where I have pending confirmations
      const relevantOffboardings: PendingOffboarding[] = [];
      let pendingCount = 0;

      allOffboardings.forEach((offboarding) => {
        if (offboarding.status !== 'completed') {
          const myAppsInOffboarding: Array<{
            appId: string;
            appName: string;
            pendingCount: number;
          }> = [];

          offboarding.applications.forEach((app) => {
            if (app.adminEmails.includes(userEmail)) {
              const myStatus = app.confirmationStatus[userEmail];
              if (!myStatus || myStatus.status === 'pending') {
                myAppsInOffboarding.push({
                  appId: app.appId,
                  appName: app.appName,
                  pendingCount: 1,
                });
                pendingCount++;
              }
            }
          });

          if (myAppsInOffboarding.length > 0) {
            relevantOffboardings.push({
              offboarding,
              myApplications: myAppsInOffboarding,
            });
          }
        }
      });

      setPendingOffboardings(relevantOffboardings);

      // Get reviews that include my applications
      const allReviews = await reviewsAPI.getAll();
      const myActiveReviews = allReviews.filter(
        (review) =>
          review.status === 'in_progress' &&
          review.applicationIds.some((appId) =>
            myApps.some((app) => app.id === appId)
          )
      );
      setMyReviews(myActiveReviews);

      setTotalPendingActions(pendingCount);
    } catch (error) {
      console.error('Error loading my tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div>Loading your tasks...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">My Tasks</h1>
            <p className="text-muted-foreground mt-1">
              Actions and reviews for your applications
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={totalPendingActions > 0 ? 'destructive' : 'secondary'}
              className="text-lg px-4 py-2"
            >
              {totalPendingActions} pending action{totalPendingActions !== 1 ? 's' : ''}
            </Badge>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">My Applications</CardTitle>
              <CheckCircle className="h-4 w-4 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{myApplications.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Applications I manage
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Offboardings</CardTitle>
              <AlertCircle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingOffboardings.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Requiring confirmation
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Reviews</CardTitle>
              <Clock className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{myReviews.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                In progress
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Offboarding Confirmations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Pending Offboarding Confirmations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingOffboardings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="font-medium">No pending confirmations</p>
                <p className="text-sm">All your offboarding requests are up to date</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingOffboardings.map((item) => (
                  <div
                    key={item.offboarding.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-orange-50/50 dark:bg-orange-900/20"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {item.offboarding.employeeName}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {item.offboarding.employeeEmail}
                        </p>
                      </div>
                      <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900/30">
                        {item.myApplications.length} app
                        {item.myApplications.length !== 1 ? 's' : ''} pending
                      </Badge>
                    </div>

                    <div className="space-y-2 mb-3">
                      <p className="text-xs font-medium text-muted-foreground">
                        Your applications requiring confirmation:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {item.myApplications.map((app) => (
                          <Badge key={app.appId} variant="outline">
                            {app.appName}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t">
                      <p className="text-xs text-muted-foreground">
                        Created {formatDate(item.offboarding.createdAt)} by{' '}
                        {item.offboarding.initiatedBy}
                      </p>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => navigate(`/verification-request/${item.offboarding.id}`)}
                      >
                        Review & Confirm
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Applications in Active Reviews */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" />
              My Applications in Active Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myReviews.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="font-medium">No active reviews</p>
                <p className="text-sm">Your applications are not currently under review</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myReviews.map((review) => {
                  const myAppsInReview = myApplications.filter((app) =>
                    review.applicationIds.includes(app.id)
                  );

                  return (
                    <div
                      key={review.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">
                            {review.title || `Review ${review.id}`}
                          </h3>
                          {review.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {review.description}
                            </p>
                          )}
                        </div>
                        <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 hover:bg-purple-100 dark:hover:bg-purple-900/30">
                          In Progress
                        </Badge>
                      </div>

                      <div className="space-y-2 mb-3">
                        <p className="text-xs font-medium text-muted-foreground">
                          Your applications in this review ({myAppsInReview.length}):
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {myAppsInReview.map((app) => (
                            <Badge key={app.id} variant="outline">
                              {app.name}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <p className="text-xs text-muted-foreground">
                          Created {formatDate(review.createdAt)}
                          {review.scheduledDate &&
                            ` • Scheduled ${formatDate(review.scheduledDate)}`}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/reviews/${review.id}`)}
                        >
                          View Review
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Applications List */}
        <Card>
          <CardHeader>
            <CardTitle>My Applications ({myApplications.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {myApplications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="font-medium">No applications assigned</p>
                <p className="text-sm">You are not currently managing any applications</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myApplications.map((app) => (
                  <div
                    key={app.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold">{app.name}</h3>
                      <Badge
                        variant="outline"
                        className={
                          app.status === 'active'
                            ? 'text-green-600 border-green-600'
                            : 'text-gray-600 dark:text-gray-400 border-gray-600'
                        }
                      >
                        {app.status}
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-muted-foreground">
                        <strong>Direction:</strong> {app.direction}
                      </p>
                      <p className="text-muted-foreground">
                        <strong>Auth:</strong> {app.authType.toUpperCase()}
                      </p>
                      <p className="text-muted-foreground">
                        <strong>2FA:</strong>{' '}
                        {app.twoFARequired ? (
                          <span className="text-green-600">Enabled</span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400">Disabled</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};
